import { useCallback, useState } from 'react';

import { updatePreferences } from '@/analytics/consent-hub/consent-client';
import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { type LocaleType } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

export type EmailSubscriptionStatus = 'idle' | 'pending' | 'success' | 'error';

export interface UseSubmitEmailSubscriptionArgs {
	formId: string;
	consentConfig: LocaleType;
	consentLabelText: string;
	privacyPolicyText: string;
}

export interface SubmitEmailSubscriptionArgs {
	email: string;
	hasMarketingOptIn: boolean;
}

export interface UseSubmitEmailSubscriptionResult {
	status: EmailSubscriptionStatus;
	submit: (args: SubmitEmailSubscriptionArgs) => Promise<void>;
	reset: () => void;
}

export const useSubmitEmailSubscription = (
	args: UseSubmitEmailSubscriptionArgs,
): UseSubmitEmailSubscriptionResult => {
	const { formId, consentConfig, consentLabelText, privacyPolicyText } = args;

	const stargateClient = useBxpWebStargateClient();
	const [status, setStatus] = useState<EmailSubscriptionStatus>('idle');

	const submit = useCallback(
		async (submitArgs: SubmitEmailSubscriptionArgs) => {
			const { email, hasMarketingOptIn } = submitArgs;

			setStatus('pending');

			try {
				const responseStatus = await updatePreferences(
					stargateClient,
					{
						formId,
						email: email.trim(),
						formUrl: window.location.href,
						site: 'atlassian',
						source: 'uniform',
						consentCheckboxValue: hasMarketingOptIn,
						consentLabelText,
						privacyPolicyText,
					},
					consentConfig,
				);

				// updateConsentPreferences resolves with the raw HTTP status rather than throwing.
				if (typeof responseStatus === 'number' && (responseStatus < 200 || responseStatus >= 300)) {
					throw new Error(`Consent preferences update failed with status ${responseStatus}`);
				}

				setStatus('success');
			} catch {
				setStatus('error');
			}
		},
		[stargateClient, formId, consentConfig, consentLabelText, privacyPolicyText],
	);

	const reset = useCallback(() => setStatus('idle'), []);

	return { status, submit, reset };
};
